import { LineChartAxisSide, type LineChartAxis, type LineChartSeries } from '@shigo-ui/viz';

import type { ReportRequest, ReportResponse } from './model';
import { reportMetricDefinitions } from './catalog';
import {
  formatReportChartAxisValue,
  formatReportMetricValue,
  numericReportValue,
  reportMetricLabel,
} from './format';
import { ReportDimension, ReportMetric, ReportMetricFormat } from './reportEnums';
import { identityMetricsForDimensions } from './reportRequest';

const chartColors = [
  'var(--chart-1)',
  'var(--chart-2)',
  'var(--chart-3)',
  'var(--chart-4)',
  'var(--chart-5)',
  'var(--chart-6)',
  'var(--chart-7)',
];

export interface ReportChartProjection {
  series: LineChartSeries[];
  axes: LineChartAxis[];
}

export function projectReportChart(
  request: ReportRequest,
  response: ReportResponse | undefined,
  timeShift?: { response: ReportResponse | undefined; days: number | null },
): ReportChartProjection {
  const identity = new Set(identityMetricsForDimensions(request.dimensions));
  const measures = request.metrics.filter(
    (metric) =>
      !identity.has(metric) && reportMetricDefinitions[metric].format !== ReportMetricFormat.String,
  );
  const rows = response?.report ?? [];
  const shiftedRows = timeShift?.days ? (timeShift.response?.report ?? []) : [];
  const axisGroups = groupMetricsByFormatAndRange(measures, [...rows, ...shiftedRows]);
  const axisIdByMetric = new Map<ReportMetric, string>();
  const axes = axisGroups.map((group, index): LineChartAxis => {
    const id = `report-axis-${String(index)}`;
    group.metrics.forEach((metric) => axisIdByMetric.set(metric, id));
    return {
      id,
      label: group.metrics.map(reportMetricLabel).join(' & '),
      labels: group.metrics.map((metric) => ({
        label: reportMetricLabel(metric),
        color: colorForMetric(measures.indexOf(metric)),
      })),
      color: colorForMetric(measures.indexOf(group.metrics[0] ?? ReportMetric.AdRequests)),
      side: index % 2 === 0 ? LineChartAxisSide.Left : LineChartAxisSide.Right,
      axisValueFormatter: (value) =>
        formatReportChartAxisValue(value, group.metrics[0] ?? ReportMetric.AdRequests),
      valueFormatter: (value) =>
        formatReportMetricValue(value, group.metrics[0] ?? ReportMetric.AdRequests),
    };
  });

  return {
    axes,
    series: measures.flatMap((metric, metricIndex) => {
      const label = reportMetricLabel(metric);
      const baseSeries: LineChartSeries = {
        id: metric,
        label,
        color: colorForMetric(metricIndex),
        axisId: axisIdByMetric.get(metric),
        data: chartSeriesData(request, rows, metric),
      };
      if (!timeShift?.days || shiftedRows.length === 0) return [baseSeries];
      return [
        baseSeries,
        {
          ...baseSeries,
          id: `${metric}:time-shift`,
          label,
          legendGroupId: metric,
          dashed: true,
          data: chartSeriesData(request, shiftedRows, metric, rows),
        },
      ];
    }),
  };
}

function chartSeriesData(
  request: ReportRequest,
  rows: ReportResponse['report'],
  metric: ReportMetric,
  alignmentRows?: ReportResponse['report'],
) {
  return rows.flatMap((row, index) => {
    const value = numericReportValue(row[reportMetricDefinitions[metric].field]);
    if (value === undefined) return [];
    const shiftedLabel = reportPointLabel(request, row);
    const alignedRow = alignmentRows?.[index];
    return [
      {
        x: index,
        label: alignedRow ? reportPointLabel(request, alignedRow) : shiftedLabel,
        ...(alignmentRows ? { tooltipLabel: shiftedLabel } : {}),
        value,
      },
    ];
  });
}

function groupMetricsByFormatAndRange(metrics: ReportMetric[], rows: ReportResponse['report']) {
  const groups: Array<{ format: ReportMetricFormat; max: number; metrics: ReportMetric[] }> = [];
  metrics.forEach((metric) => {
    const definition = reportMetricDefinitions[metric];
    const maxValue = Math.max(
      0,
      ...rows.flatMap((row) => {
        const value = numericReportValue(row[definition.field]);
        return value === undefined ? [] : [Math.abs(value)];
      }),
    );
    const compatible = groups.find(
      (group) => group.format === definition.format && rangesAreCompatible(group.max, maxValue),
    );
    if (compatible) {
      compatible.metrics.push(metric);
      compatible.max = Math.max(compatible.max, maxValue);
    } else groups.push({ format: definition.format, max: maxValue, metrics: [metric] });
  });
  return groups;
}

function rangesAreCompatible(left: number, right: number) {
  if (left === 0 || right === 0) return true;
  return Math.max(left, right) / Math.min(left, right) <= 4;
}

function reportPointLabel(request: ReportRequest, row: ReportResponse['report'][number]) {
  const identityMetrics = request.dimensions.includes(ReportDimension.Hour)
    ? [ReportMetric.Hour]
    : identityMetricsForDimensions(request.dimensions);
  const fields = identityMetrics.map((metric) => {
    const value = row[reportMetricDefinitions[metric].field];
    return value === null || value === undefined || value === ''
      ? ''
      : formatReportMetricValue(value, metric);
  });
  return fields.filter((value) => value !== '').join(' ') || '-';
}

function colorForMetric(index: number) {
  return chartColors[index % chartColors.length] ?? 'var(--foreground)';
}
